import { Link } from "react-router-dom";
import { DetailSection } from "./detailBits";
import {
  SellerPrequalificationSurface,
  useSellerPrequalAdapterForSubmission,
} from "./prequalification/SellerPrequalificationSurface";

/**
 * The seller's prequalification, addressed by the SUBMISSION a specialist is
 * standing on.
 *
 * <p>🔴 <b>A MOUNT, not an implementation.</b> The screen itself is
 * <c>SellerPrequalificationSurface</c>, shared with the listing's own
 * Seller prequalification tab — same rows, same lifecycle vocabulary, same
 * three-state read. This file supplies the submission adapter and the one
 * sentence the submission address needs to say about itself.</p>
 *
 * <p>🔴 <b>THE PROPS ARE UNCHANGED, DELIBERATELY.</b> This has two live mounts
 * — the submission's Escalation tab, where the adjacency to the escalation gate
 * is behavioural, and the readiness page being built over that panel — and
 * re-keying it to a listing id would break both at once for no gain the surface
 * itself needs. The submission id is not vestigial: it is the ADDRESS, and the
 * address is what decides the gate. A submission-addressed write takes the
 * buyer's own per-row ownership rule; the listing-addressed twin takes
 * <c>Listings.Manage</c> with no row check, which is <c>ListingsController</c>'s
 * own documented rule. Resolving this into the listing address would let a
 * specialist who does not own the buyer send a seller invitation from here — a
 * widening arriving as a side effect of a UI change rather than as a decision
 * anyone made.</p>
 *
 * <p>🔴 <b>The link attaches to the LISTING, and the panel says so out loud
 * — with the count.</b> A seller prequalification is a fact about the business,
 * so acting here serves every other buyer submitted against the same listing.
 * That sentence matters MORE now than it did, not less: the surface that owns
 * this fact is the listing's, and the reader about to follow the link is exactly
 * the person who needs to know the edit reaches other buyers.
 * <c>otherSubmissionCount</c> is submission-relative and this is its one
 * consumer.</p>
 *
 * <p>🔴 <b>The status is rendered exactly as the server gives it, and expiry is
 * NEVER recomputed here</b> — see <c>inviteBits</c>.</p>
 */
export function SellerPrequalificationPanel({
  submissionId,
  canEdit,
}: {
  submissionId: string;
  canEdit: boolean;
}) {
  const adapter = useSellerPrequalAdapterForSubmission(submissionId);

  return (
    <DetailSection title="Seller prequalification">
      <SellerPrequalificationSurface
        adapter={adapter}
        canEdit={canEdit}
        intro={<SharedReachNote adapter={adapter} />}
      />
    </DetailSection>
  );
}

/**
 * Whose fact this is, and how far a change to it reaches.
 *
 * <p>Rendered only once the read has SUCCEEDED — a count printed from an
 * undefined payload reads as "no other buyers", which is the one wrong thing to
 * say to someone about to change a shared record.</p>
 */
function SharedReachNote({
  adapter,
}: {
  adapter: ReturnType<typeof useSellerPrequalAdapterForSubmission>;
}) {
  const l = adapter.status.data;
  if (adapter.status.loading || adapter.status.error || !l) return null;

  // The listing's canonical label, not its raw business-name column: a listing
  // whose seller has not named the business has a headline, and printing the
  // blank column would drop it out of the ladder every other surface walks.
  const business = l.listingDisplayName?.trim() || l.listingBusinessName?.trim();
  const others = l.otherSubmissionCount;

  return (
    <p className="m-0 text-caption text-gray-mid">
      Whether Phoenix has established that {business || "this listing"}&apos;s seller
      can carry a note on these terms. It belongs to the listing, not to this
      submission
      {others > 0
        ? `, so anything changed here also serves the ${others} other ${
            others === 1 ? "buyer" : "buyers"
          } submitted against this business.`
        : "."}{" "}
      <Link
        to={`/listings/${l.listingId}?tab=seller-prequalification`}
        className="text-gold-shadow hover:underline"
        data-testid="seller-prequal-open-listing"
      >
        Open it on the listing →
      </Link>
    </p>
  );
}
